import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Mail, ArrowRight, ShieldCheck, Smartphone, Eye, EyeOff } from "lucide-react";
import { getApiUrl } from "@/lib/utils";

const requestOtpSchema = z.object({
  identifier: z.string().min(3, "Please enter your email or mobile number"),
});

const verifyOtpSchema = z.object({
  otp: z.string().length(6, "OTP must be 6 digits"),
});

const resetPasswordSchema = z.object({
  newPassword: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RequestOtpValues = z.infer<typeof requestOtpSchema>;
type VerifyOtpValues = z.infer<typeof verifyOtpSchema>;
type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [identifier, setIdentifier] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<'email' | 'whatsapp'>('email');
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { register: register1, handleSubmit: handleSubmit1, formState: { errors: errors1 } } = useForm<RequestOtpValues>({
    resolver: zodResolver(requestOtpSchema),
  });

  const { register: register2, handleSubmit: handleSubmit2, formState: { errors: errors2 } } = useForm<VerifyOtpValues>({
    resolver: zodResolver(verifyOtpSchema),
  });

  const { register: register3, handleSubmit: handleSubmit3, formState: { errors: errors3 } } = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onRequestOtp = async (data: RequestOtpValues) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/auth/forgot-password-owner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: data.identifier, deliveryMethod })
      });
      const result = await response.json();
      if (response.ok) {
        setIdentifier(data.identifier);
        setStep(2);
        toast.success(result.message || "OTP sent successfully!");
      } else {
        toast.error(result.message || "Failed to send OTP");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const onVerifyOtp = async (data: VerifyOtpValues) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/auth/verify-otp-owner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, otp: data.otp })
      });
      const result = await response.json();
      if (response.ok) {
        setOtp(data.otp);
        setStep(3);
        toast.success("OTP Verified!");
      } else {
        toast.error(result.message || "Invalid OTP");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const onResetPassword = async (data: ResetPasswordValues) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/auth/reset-password-owner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, otp, newPassword: data.newPassword })
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("Password reset successfully! Please login.");
        navigate("/login");
      } else {
        toast.error(result.message || "Failed to reset password");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <KeyRound className="h-6 w-6 text-primary" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            {step === 1 ? "Forgot Password" : step === 2 ? "Verify OTP" : "Reset Password"}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {step === 1 
              ? `Enter your registered ${deliveryMethod === 'email' ? 'email address' : 'mobile number'} to receive an OTP.` 
              : step === 2 
              ? `Enter the 6-digit OTP sent to ${identifier}`
              : "Enter your new password below."}
          </p>
        </div>

        {step === 1 && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit1(onRequestOtp)}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="identifier">{deliveryMethod === 'email' ? 'Email Address' : 'WhatsApp Number'}</Label>
                <div className="relative mt-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {deliveryMethod === 'email' ? (
                      <Mail className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Smartphone className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <Input 
                    id="identifier" 
                    placeholder={deliveryMethod === 'email' ? "admin@example.com" : "9876543210"} 
                    className="pl-10" 
                    {...register1("identifier")} 
                  />
                </div>
                {errors1.identifier && <p className="text-sm text-red-500 mt-1">{errors1.identifier.message}</p>}
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Sending..." : `Send OTP via ${deliveryMethod === 'email' ? 'Email' : 'WhatsApp'}`} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            
            <div className="text-center mt-4">
              <button 
                type="button" 
                onClick={() => setDeliveryMethod(deliveryMethod === 'email' ? 'whatsapp' : 'email')}
                className="text-sm font-medium text-primary hover:underline transition-all"
              >
                {deliveryMethod === 'email' ? 'Try with Mobile Number instead' : 'Try with Email Address instead'}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit2(onVerifyOtp)}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="otp">Enter OTP</Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <ShieldCheck className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input 
                    id="otp" 
                    placeholder="123456" 
                    maxLength={6}
                    className="pl-10 text-center tracking-widest text-lg" 
                    {...register2("otp")} 
                  />
                </div>
                {errors2.otp && <p className="text-sm text-red-500 mt-1">{errors2.otp.message}</p>}
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Verifying..." : "Verify OTP"}
            </Button>
            <p className="text-center text-sm">
              <button type="button" onClick={() => setStep(1)} className="text-primary hover:underline">Change Email/Mobile</button>
            </p>
          </form>
        )}

        {step === 3 && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit3(onResetPassword)}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input 
                    id="newPassword" 
                    type={showPassword ? "text" : "password"} 
                    {...register3("newPassword")} 
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors3.newPassword && <p className="text-sm text-red-500 mt-1">{errors3.newPassword.message}</p>}
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input 
                    id="confirmPassword" 
                    type={showConfirmPassword ? "text" : "password"} 
                    {...register3("confirmPassword")} 
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors3.confirmPassword && <p className="text-sm text-red-500 mt-1">{errors3.confirmPassword.message}</p>}
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        )}

        {step === 1 && (
          <div className="text-center mt-4">
            <Link to="/login" className="text-sm text-primary hover:underline">
              Back to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
